/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from './View'
import { BoxHeaderView } from './BoxHeaderView'
import { Icon } from '../icons/Icon'
import { Pressable } from '../button/Pressable'
import { mono, boSize, boMedia } from '@components/systems/backoffice/backoffice.tokens'
import { useBoSizeClass } from '@components/systems/backoffice/useBoSizeClass'
import { BoSheet } from '@components/systems/backoffice/BoSheet'

interface PropsType {
	title?: string
	showHeader?: false
	renderTitle?: React.ReactNode
	className?: string
	children?: React.ReactNode
	leftHeaderAccessory?: JSX.Element
	rightHeaderAccessory?: React.ReactNode
	/** 유지용 — Monolith 에서는 박스에 배경을 칠하지 않는다(무시) */
	backgroundColor?: string
}

/**
 * 백오피스 페이지 프레임. 헤더(제목 + 액션) → 헤어라인 → 본문.
 * 배경·라운드·그림자 없음 — 페이지 그라운드 위에 헤어라인만 남긴다(DESIGN_SYSTEM §4).
 *
 * 폰(<600, RESPONSIVE_PLAN §3.2): 좌측 도구(필터 바)는 헤더에서 빠져 **바텀 시트**로 들어가고,
 * 헤더에는 「필터」 버튼 하나가 선다. 높이 체인은 유지한다 — 카드 목록(가상 리스트)이 남은 높이를 받아야 한다.
 */
export const BoxView = (props: PropsType) => {
	// variables
	const showHeader: boolean = props.showHeader ?? true
	const sizeClass = useBoSizeClass()
	const isPhone = sizeClass === 'phone'
	const [filterOpen, setFilterOpen] = React.useState(false)

	// 폰·태블릿에서 페이지 헤더는 스크롤 위에 붙어 있고(§4.1 상단 바), 그 아래에 목록의 검색 줄이 또 붙는다.
	// 검색 줄이 헤더를 가리지 않도록 헤더 높이를 변수로 내려 준다 — 제목이 두 줄이 되면 값도 따라간다.
	const rootRef = React.useRef<HTMLDivElement | null>(null)
	React.useLayoutEffect(() => {
		const root = rootRef.current
		if (root === null) return
		if (sizeClass === 'desktop') {
			root.style.removeProperty('--boHeaderH')
			return
		}
		const header = root.querySelector('.boBoxHeader')
		if (header === null) return
		const apply = () =>
			root.style.setProperty(
				'--boHeaderH',
				`${Math.round(header.getBoundingClientRect().height)}px`,
			)
		apply()
		const observer = new ResizeObserver(apply)
		observer.observe(header)
		return () => observer.disconnect()
	}, [sizeClass, showHeader])

	const filterButton =
		isPhone && props.leftHeaderAccessory ? (
			<Pressable
				className="boBoxFilterBtn"
				onClick={() => setFilterOpen(true)}
				aria-label="필터"
			>
				<Icon name="tune-variant" className="boBoxFilterIcon" />
				<span className="boBoxFilterLabel">필터</span>
			</Pressable>
		) : null

	return (
		<View
			ref={rootRef}
			css={style}
			className={`boBox ${sizeClass} ${props.className ?? ''}`}
		>
			{showHeader ? (
				<BoxHeaderView
					title={props.title}
					renderTitle={props.renderTitle}
					leftHeaderAccessory={
						isPhone ? filterButton ?? undefined : props.leftHeaderAccessory
					}
					rightHeaderAccessory={props.rightHeaderAccessory}
				/>
			) : null}
			<View className="boxChildrenView">{props.children}</View>

			{isPhone && props.leftHeaderAccessory ? (
				<BoSheet
					open={filterOpen}
					onClose={() => setFilterOpen(false)}
					title="필터"
					className="boBoxFilterSheet"
					footer={
						<button type="button" className="boBtn" onClick={() => setFilterOpen(false)}>
							닫기
						</button>
					}
				>
					<View className="boBoxFilterSheetBody" css={filterSheetStyle}>
						{props.leftHeaderAccessory}
					</View>
				</BoSheet>
			) : null}
		</View>
	)
}

const style = css({
	display: 'flex',
	flexDirection: 'column',
	minHeight: '100%',
	height: '100%',
	padding: `0 ${boSize.gutter}`,
	background: 'transparent',
	borderRadius: 0,
	color: mono.ink,
	boxSizing: 'border-box',

	'.boxChildrenView': {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'flex-start',
		flex: '1 1 auto',
		minHeight: 0,
		width: '100%',
		paddingTop: 20,
		paddingBottom: 20,
		overflow: 'auto',
	},

	'.boBoxFilterBtn': {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		height: 40,
		padding: '0 12px 0 10px',
		border: `1px solid ${mono.lineSoft}`,
		borderRadius: 2,
		fontSize: 14,
		fontWeight: 500,
		color: mono.ink,
		'.boBoxFilterIcon': { fontSize: 20, color: mono.ink },
	},

	[boMedia.phone]: {
		padding: '0 16px',
		// 페이지가 스크롤러다 — 박스는 화면 높이에 갇히지 않는다 (스크롤은 한 화면에 하나)
		height: 'auto',
		minHeight: '100%',
		maxWidth: '100%',
		'.boxChildrenView': {
			// 페이지 본문이 둘 이상(정산 = 집계 + 목록)이면 폰에서는 세로로 쌓는다
			flexDirection: 'column',
			alignItems: 'stretch',
			paddingTop: 0,
			paddingBottom: 0,
			minWidth: 0,
			maxWidth: '100%',
			overflow: 'visible',
		},
	},
	[boMedia.tablet]: {
		padding: '0 20px',
	},
})

/** 필터 시트 안 — 페이지의 가로 필터 바를 세로로 눕힌다. 컨트롤은 전폭·44. 시트는 포털이라 여기 따로 건다 */
const filterSheetStyle = css({
	'& > *': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		gap: 16,
		width: '100%',
	},
	'& > * > *': { width: '100%', marginRight: '0 !important' },
	// 필터 바 안의 중첩 래퍼(오더의 .orderGridSearchView 등)도 세로로, 같은 간격
	'& > * > div:not(.MuiFormControl-root)': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		gap: 16,
	},
	'& .MuiFormControl-root, & .MuiTextField-root, & .datePickerView, & .datePicker': {
		width: '100% !important',
		minWidth: '0 !important',
	},
	'& .datePickerView': {
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
		'& .datePickerText': { display: 'none' },
	},
	'& .MuiInputBase-root': { minHeight: 44, fontSize: 15 },
	'& .boBtn, & .boBtnGhost': { minHeight: 44, width: '100%' },
})
